import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import React, { useCallback, useMemo } from 'react'

import { AssignmentDocumentDTO } from 'models/assignment'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import { FilterTab } from 'components/common/FilterTab'
import JSZip from 'jszip'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { Nullable } from 'models/helpers'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { Stack } from '@mui/material'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { saveAs } from 'file-saver'
import { useClientProperty } from 'components/pages/Property/ClientProperty/_main/contexts'
import { useGalleryAssignment } from '../../contexts'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

/** @interface Props for the ClientGalleryDocumentFiles component. */
interface Props {
  /** Document status history data to display */
  assignmentDocumentsData: AssignmentDocumentDTO[]
  /** Currently selected document */
  selectedDoc?: Nullable<AssignmentDocumentDTO>
  /** Function to set the selected document */
  setSelectedDoc: (doc: AssignmentDocumentDTO) => void
  /** Current assignment ID */
  assignmentId: string
}

/*
 * ClientGalleryDocumentFiles component displays the provided files in the client gallery.
 *
 * @example <ClientGalleryDocumentFiles assignmentDocumentsData={[]} />
 */
export const ClientGalleryDocumentFiles: React.FC<Props> = ({
  assignmentDocumentsData,
  selectedDoc,
  setSelectedDoc,
  assignmentId
}) => {

  const { t } = useTranslation(['gallery'])
  const { pathname } = useLocation()
  const { propertyData } = useClientProperty()
  const { product } = useGalleryAssignment()

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const downloadAllFiles = async () => {
    const zip = new JSZip()
    const folder = zip.folder(`${assignmentId}`)

    for (const doc of assignmentDocumentsData) {
      const response = await fetch(doc.signedUrl.signedURL)
      const blob = await response.blob()
      folder?.file(`${doc.signedUrl.filename}`, blob)
    }

    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, `${assignmentId}.zip`)
  }

  const logDownloadDocumentAnalytics = useCallback(() => {
    if (isPropertyPathname) {
      logAnalyticsEvent(AnalyticsEvent.PROPERTY_DOWNLOAD_DOCUMENTS, {
        propertyName: propertyData?.title,
        propertyId: propertyData?.id,
        propertyType: propertyData?.propertyType,
        productType: product?.type,
        productKind: product?.kind,
        productCategory: product?.category,
      })
    }
  }, [isPropertyPathname, product?.category, product?.kind, product?.type, propertyData?.id, propertyData?.propertyType, propertyData?.title])

  return (
    <SectionedBorderBox title={t('provided_files')}>

      <Stack gap="1rem">
        {assignmentDocumentsData.map((doc, index) => {
          return (
            <FilterTab
              key={doc.name}
              isActive={selectedDoc?.name === doc.name}
              height="4rem"
              label={doc.signedUrl.filename}
              onClick={() => setSelectedDoc(doc)}
            />
          )
        })}

      </Stack>

      <MUIDivider margin={12} />

      <MUIButton
        fullWidth
        type="secondaryBorder"
        size="md"
        startIcon={<CloudDownloadOutlinedIcon fontSize="medium" />}
        onClick={() => {
          downloadAllFiles()
          logDownloadDocumentAnalytics()
        }}
      >
        {t('download_all_files')}
      </MUIButton>

    </SectionedBorderBox>
  )
}
